import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { getStringWithoutHash } from 'utilitys/helpers/general';

import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import useTableCurrentState from 'hooks/useTableCurrentState';

import { CustomizedTabs } from 'components/common/CustomizedTabs/CustomizedTabs';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { useMutationSetWidgetColumns } from 'components/Dashboards/Project/Components/CustomDashboard/queries/useQuerySelectedTableColumns';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TableFooter } from 'components/Dashboards/Project/Components/VersionCompare/TableFotter/TableFooter';
import { useMutationExportCsvReport } from 'components/Dashboards/Project/Components/VersionCompare/queries/tableDataMutation';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import {
    TKeys,
    arrayBufferToString,
    generateTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { maxPageSize, smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';
import { projectConfig } from 'components/Dashboards/Project/ProjectWrapper/projectConfig';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { CriticalPathFilter } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/filters/criticalPathFilter';
import { StatusFilter } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/filters/statusFilter';
import {
    ITableLocalFiltersFields,
    TableLocalFilters,
} from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/tableLocalFilters';
import { statusOptions } from 'components/widgets/KeyActivities/components/filter.utils';
import { useQueryRelationshipCountChangesTableData } from 'components/widgets/RelationshipCountChanges/queries/useQueryRelationshipCountChangesTableData';
import { columns, initialState } from 'components/widgets/RelationshipCountChanges/relationshipCountChanges.utils';

const RelationshipCountChanges = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    filter,
    globalFilterData,
    isSinglePage = false,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: ITableWidgetWrapperProps) => {
    const tableRef = useRef(null);
    const elementId = 'relationships-count-changes';
    const id = 'relationshipCountChanges';
    const route = `${getStringWithoutHash(window.location.href)}#${id}`;
    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });
    const componentKey = 'topRelationshipsCountChanges';
    const updatedInitialState = useTableCurrentState({ initialState, componentKey, widgetId });
    const [spinnerOpen, setSpinnerOpen] = useState<boolean>(false);
    const mutation = useMutationExportCsvReport();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters
                : getFiltersData({
                      [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
                      [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
                      [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                  }),

        [filter, globalFilterData],
    );

    const { data, isLoading, isFetching } = useQueryRelationshipCountChangesTableData({
        projectId: projectId,
        contractId: contractId,
        latestVersionId: latestVersionId,
        compareVersionId: compareVersionId,
        filterData: {
            filters,
        },
    });

    const title = 'Top Relationships Count Changes';
    const reportType = data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId,
        contractId,
        latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });
    const [pageSize, setPageSize] = useState(smallPageSize);
    const finalPageSize = isSinglePage ? maxPageSize : pageSize;
    const [activeTab, setActiveTab] = useState<TKeys>(
        widgetId && localFilters?.activeTab ? (localFilters?.activeTab as TKeys) : 'next',
    );
    const tabsArrData = generateTabs({ data });

    // ============================== Local Filters
    const { control, setValue, watch } = useForm<ITableLocalFiltersFields>({
        defaultValues: {
            status: statusOptions,
            criticalPath: false,
        },
    });

    const formValues = useWatch({ control });

    const activeData = useMemo(() => {
        if (data && data.periodItemsList) {
            const periodItemsList = data.periodItemsList;
            return periodItemsList.find((item) => item.id === activeTab)?.itemsList || periodItemsList[0].itemsList;
        }
        return [];
    }, [data, activeTab]);

    const filteredData = useMemo(() => {
        const status = widgetId ? localFilters?.status : formValues.status;
        const criticalPath = widgetId ? localFilters?.criticalPath : formValues.criticalPath;
        const statusArray = status?.map((item) => item.value);

        if (activeData) {
            return activeData
                .filter((item) => statusArray?.includes(item.latestVersion.cardBoardViewResponse.status.name))
                .filter((item) =>
                    criticalPath
                        ? item.latestVersion.cardBoardViewResponse.cardIndicators.isCriticalPath.isIndicatorOn ===
                          criticalPath
                        : item,
                );
        }
        return null;
    }, [activeData, localFilters, formValues]);
    // ============================== Local Filters

    const { mutate: mutateSetWidgetColumns } = useMutationSetWidgetColumns();
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);

    const formattedLocalFilters = useMemo(() => {
        return widgetId && localFilters
            ? getFiltersData({
                  [FilterNames.OPTIONS_STATUS]: localFilters?.status?.map((item) => item.label) || [],
                  [FilterNames.CRITICAL_PATH_USER_CARD_BOARD]: localFilters?.criticalPath ? ['Yes'] : ['No'],
              })
            : [];
    }, [widgetId, localFilters]);

    const mergedFilters = useMemo(() => {
        return widgetId && filter?.filters
            ? {
                  filters: [...filter?.filters, ...formattedLocalFilters],
              }
            : undefined;
    }, [widgetId, filter, formattedLocalFilters]);

    const { generateFilters: formattedFilter } = useGlobalFiltersHook({
        widgetId,
        filter: mergedFilters,
        latestVersionId,
    });
    const handleAPIExcelExport = () => {
        setSpinnerOpen(true);
        mutation.mutate(
            {
                compareVersionId,
                contractId,
                filter: { filters },
                latestVersionId,
                period: activeTab,
                projectId,
                reportType,
                fileName,
            },
            {
                onSuccess: () => {
                    setSpinnerOpen(false);
                    handleSnackBar('File Successfully Downloaded', 'success');

                    sendEventWithDimensions({
                        category: 'Export',
                        action: 'Excel',
                        label: `${title} ${subTitle}`,
                    });
                },
                onError: (error) => {
                    setSpinnerOpen(false);
                    const message = arrayBufferToString(error?.response?.data);
                    DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
                },
            },
        );
    };

    const setWidgetColumns = (widgetId: string) => {
        if (tableRef.current) {
            const { hiddenColumns, columnOrder } = tableRef.current.state;
            mutateSetWidgetColumns(
                { columns: { hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };
    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title });
    }, [widgetId, subTitle, title]);

    return (
        <WidgetWithTitle
            title={title}
            tooltip={data?.info}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && formattedFilter.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={formattedFilter} />
                ) : (
                    <TableLocalFilters key={'filters'}>
                        <StatusFilter control={control} />
                        <CriticalPathFilter value={watch('criticalPath')} setValue={setValue} />
                    </TableLocalFilters>
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    elementId={elementId}
                    widgetId={widgetId}
                    route={route}
                    filters={filters}
                    localFilters={{ ...formValues, activeTab: activeTab }}
                    callBack={setWidgetColumns}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown
                    title={title}
                    key={'export'}
                    subTitle={subTitle}
                    handleAPIExcelExport={handleAPIExcelExport}
                />,
            ]}
        >
            {filteredData && (
                <div className={classes.tableWrapperContainer}>
                    {tabsArrData.length > 0 && (
                        <CustomizedTabs<TKeys> tabs={tabsArrData} activeTab={activeTab} setActiveTab={setActiveTab} />
                    )}

                    {updatedInitialState && (
                        <MainTable
                            getInstanceCallback={(instance) => (tableRef.current = instance)}
                            data={filteredData}
                            columns={columns}
                            initialState={updatedInitialState}
                            pageSize={finalPageSize}
                            noData={<NoData>No Changes To Display</NoData>}
                            isLoading={isLoading || isFetching}
                        />
                    )}

                    {filteredData.length > smallPageSize && !isSinglePage && (
                        <TableFooter
                            setPageSize={setPageSize}
                            pageSize={pageSize}
                            link={!widgetId ? projectConfig.relationshipCountChanges.link : undefined}
                        />
                    )}
                </div>
            )}
            {tableRef?.current && (
                <CustomTableColumnsModal
                    onHideModal={() => setModalColumnsOpen(false)}
                    showModal={modalColumnsOpen}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    tableInstance={tableRef.current}
                    initialState={initialState}
                    columns={columns}
                />
            )}
            {spinnerOpen && <OverlayWithSpinner />}
        </WidgetWithTitle>
    );
};
export default React.memo(RelationshipCountChanges);
